import React from 'react';
import { useTranslation } from 'react-i18next';

const Offers: React.FC = () => {
  const { t } = useTranslation();
  
  return (
    <div className="container-custom section-padding">
      <div className="text-center mb-12">
        <h1 className="text-4xl md:text-5xl font-bold text-mypa-blue mb-4">
          {t("offers.title") || "Nos offres"}
        </h1>
        <p className="text-xl max-w-3xl mx-auto">
          {t("offers.subtitle") || "Des solutions adaptées à chaque étape de votre parcours"}
        </p>
      </div>
      
      {/* Offers Comparison Table */}
      <div className="overflow-x-auto mb-16">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className="p-4 border-b-2 border-mypa-green"></th>
              <th className="p-4 border-b-2 border-mypa-green bg-mypa-light text-mypa-blue text-xl font-bold">
                {t("offers.discovery.title") || "Découverte"}
              </th>
              <th className="p-4 border-b-2 border-mypa-green bg-mypa-light text-mypa-blue text-xl font-bold">
                {t("offers.performance.title") || "Performance"}
              </th>
              <th className="p-4 border-b-2 border-mypa-green bg-mypa-light text-mypa-blue text-xl font-bold">
                {t("offers.strategy.title") || "Stratégie"}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="p-4 border-b border-gray-200 font-bold">Description</td>
              <td className="p-4 border-b border-gray-200">{t("offers.discovery.description") || "Premier pas vers la décarbonation"}</td>
              <td className="p-4 border-b border-gray-200">{t("offers.performance.description") || "Accompagnement complet"}</td>
              <td className="p-4 border-b border-gray-200">{t("offers.strategy.description") || "Transformation profonde"}</td>
            </tr>
            <tr>
              <td className="p-4 border-b border-gray-200 font-bold">Idéal pour</td>
              <td className="p-4 border-b border-gray-200">Organisations débutant leur démarche de décarbonation</td>
              <td className="p-4 border-b border-gray-200">Organisations souhaitant réduire significativement leur empreinte</td>
              <td className="p-4 border-b border-gray-200">Organisations visant une transformation profonde</td>
            </tr>
            <tr>
              <td className="p-4 border-b border-gray-200 font-bold">Durée typique</td>
              <td className="p-4 border-b border-gray-200">1-2 mois</td>
              <td className="p-4 border-b border-gray-200">6-12 mois</td>
              <td className="p-4 border-b border-gray-200">12-24 mois</td>
            </tr>
            <tr>
              <td className="p-4 border-b border-gray-200 font-bold">Niveau d'accompagnement</td>
              <td className="p-4 border-b border-gray-200">
                <div className="flex">
                  <span className="text-mypa-green">●</span>
                  <span className="text-gray-300">●●</span>
                </div>
              </td>
              <td className="p-4 border-b border-gray-200">
                <div className="flex">
                  <span className="text-mypa-green">●●</span>
                  <span className="text-gray-300">●</span>
                </div>
              </td>
              <td className="p-4 border-b border-gray-200">
                <div className="flex">
                  <span className="text-mypa-green">●●●</span>
                </div>
              </td>
            </tr>
            <tr>
              <td className="p-4 border-b border-gray-200 font-bold">Accès plateforme</td>
              <td className="p-4 border-b border-gray-200">Limité</td>
              <td className="p-4 border-b border-gray-200">Complet</td>
              <td className="p-4 border-b border-gray-200">Premium</td>
            </tr>
            <tr>
              <td className="p-4 border-b border-gray-200"></td>
              <td className="p-4 border-b border-gray-200">
                <a href="/contact" className="btn-outline text-sm">En savoir plus</a>
              </td>
              <td className="p-4 border-b border-gray-200">
                <a href="/contact" className="btn-outline text-sm">En savoir plus</a>
              </td>
              <td className="p-4 border-b border-gray-200">
                <a href="/contact" className="btn-outline text-sm">En savoir plus</a>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      
      {/* Detailed Offers */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
        {/* Discovery Offer */}
        <div className="bg-white p-6 rounded-lg shadow-md border-t-4 border-mypa-green">
          <h2 className="text-2xl font-bold text-mypa-blue mb-4">{t("offers.discovery.title") || "Découverte"}</h2>
          <p className="mb-6">{t("offers.discovery.description") || "Premier pas vers la décarbonation avec un diagnostic initial et des recommandations"}</p>
          
          <h3 className="text-lg font-bold text-mypa-green mb-3">Inclus :</h3>
          <ul className="space-y-2 mb-6">
            {(t("offers.discovery.features", { returnObjects: true }) as string[] || [
              "Bilan carbone simplifié",
              "Identification des opportunités",
              "Recommandations prioritaires",
              "Accès limité à la plateforme"
            ]).map((feature: string, index: number) => (
              <li key={index} className="flex items-start">
                <div className="text-mypa-green mr-2">✓</div>
                <span>{feature}</span>
              </li>
            ))}
          </ul>
          
          <div className="mt-auto">
            <a href="/contact" className="btn-primary w-full text-center">Nous contacter</a>
          </div>
        </div>
        
        {/* Performance Offer */}
        <div className="bg-white p-6 rounded-lg shadow-md border-t-4 border-mypa-blue transform scale-105">
          <div className="bg-mypa-blue text-white text-xs font-bold py-1 px-3 rounded-full inline-block mb-2">Recommandé</div>
          <h2 className="text-2xl font-bold text-mypa-blue mb-4">{t("offers.performance.title") || "Performance"}</h2>
          <p className="mb-6">{t("offers.performance.description") || "Accompagnement complet pour réduire significativement votre empreinte"}</p>
          
          <h3 className="text-lg font-bold text-mypa-green mb-3">Inclus :</h3>
          <ul className="space-y-2 mb-6">
            {(t("offers.performance.features", { returnObjects: true }) as string[] || [
              "Bilan carbone détaillé",
              "Plan d'action personnalisé",
              "Accompagnement à la mise en œuvre",
              "Accès complet à la plateforme",
              "Suivi trimestriel des indicateurs"
            ]).map((feature: string, index: number) => (
              <li key={index} className="flex items-start">
                <div className="text-mypa-green mr-2">✓</div>
                <span>{feature}</span>
              </li>
            ))}
          </ul>
          
          <div className="mt-auto">
            <a href="/contact" className="btn-primary w-full text-center">Nous contacter</a>
          </div>
        </div>
        
        {/* Strategy Offer */}
        <div className="bg-white p-6 rounded-lg shadow-md border-t-4 border-mypa-green">
          <h2 className="text-2xl font-bold text-mypa-blue mb-4">{t("offers.strategy.title") || "Stratégie"}</h2>
          <p className="mb-6">{t("offers.strategy.description") || "Transformation profonde de votre organisation vers un modèle bas carbone"}</p>
          
          <h3 className="text-lg font-bold text-mypa-green mb-3">Inclus :</h3>
          <ul className="space-y-2 mb-6">
            {(t("offers.strategy.features", { returnObjects: true }) as string[] || [
              "Stratégie carbone intégrée",
              "Accompagnement dirigeants",
              "Formation des équipes",
              "Intégration dans la stratégie d'entreprise",
              "Reporting et communication",
              "Accès premium à la plateforme"
            ]).map((feature: string, index: number) => (
              <li key={index} className="flex items-start">
                <div className="text-mypa-green mr-2">✓</div>
                <span>{feature}</span>
              </li>
            ))}
          </ul>
          
          <div className="mt-auto">
            <a href="/contact" className="btn-primary w-full text-center">Nous contacter</a>
          </div>
        </div>
      </div>
      
      {/* Economic Models Section */}
      <section className="mb-16">
        <h2 className="text-3xl font-bold text-mypa-blue mb-6 text-center">Choisissez votre modèle économique</h2>
        <p className="text-lg mb-8 text-center max-w-3xl mx-auto">
          Pour chacune de nos offres, vous pouvez choisir le modèle économique qui correspond le mieux à vos préférences et à votre situation.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-xl font-bold text-mypa-blue mb-3">Abonnement Classique</h3>
            <p className="mb-4">Accès complet à notre plateforme et à nos services avec un abonnement mensuel ou annuel.</p>
            <p className="text-sm text-gray-600">Idéal pour : Organisations préférant un coût fixe et prévisible</p>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-xl font-bold text-mypa-blue mb-3">Performance Carbone</h3>
            <p className="mb-4">Paiement basé sur les réductions d'émissions carbone réellement atteintes.</p>
            <p className="text-sm text-gray-600">Idéal pour : Organisations souhaitant lier le coût aux résultats</p>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-xl font-bold text-mypa-blue mb-3">Valeur Partagée</h3>
            <p className="mb-4">Partage des économies financières générées par la réduction de votre empreinte.</p>
            <p className="text-sm text-gray-600">Idéal pour : Organisations cherchant à financer leur démarche par les économies réalisées</p>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-xl font-bold text-mypa-blue mb-3">Contribution Connaissance</h3>
            <p className="mb-4">Accès gratuit en échange de données anonymisées pour enrichir notre base de connaissances.</p>
            <p className="text-sm text-gray-600">Idéal pour : Organisations avec des ressources limitées mais prêtes à partager leurs données</p>
          </div>
        </div>
      </section>
      
      {/* FAQ Section */}
      <section className="bg-mypa-light p-8 rounded-lg mb-16">
        <h2 className="text-3xl font-bold text-mypa-blue mb-6">Questions fréquentes</h2>
        
        <div className="space-y-4">
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <h3 className="text-lg font-bold text-mypa-green mb-2">Comment choisir l'offre qui me convient ?</h3>
            <p>Nous vous recommandons de commencer par un échange avec notre équipe qui vous aidera à identifier l'offre la plus adaptée à votre situation, vos objectifs et votre niveau de maturité.</p>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <h3 className="text-lg font-bold text-mypa-green mb-2">Puis-je évoluer d'une offre à l'autre ?</h3>
            <p>Absolument ! Nos offres sont conçues pour vous accompagner dans votre progression. Vous pouvez commencer par l'offre Découverte puis évoluer vers Performance ou Stratégie à mesure que votre démarche se développe.</p>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <h3 className="text-lg font-bold text-mypa-green mb-2">Les offres sont-elles personnalisables ?</h3>
            <p>Oui, chaque offre peut être ajustée pour répondre à vos besoins spécifiques. Contactez-nous pour discuter de vos exigences particulières.</p>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <h3 className="text-lg font-bold text-mypa-green mb-2">Comment fonctionnent les modèles économiques alternatifs ?</h3>
            <p>Chaque modèle économique a ses propres modalités. Par exemple, pour le modèle Performance Carbone, nous établissons ensemble une baseline et des objectifs, puis notre rémunération est liée aux réductions d'émissions réellement atteintes.</p>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="text-center">
        <h2 className="text-3xl font-bold text-mypa-blue mb-6">Prêt à démarrer ?</h2>
        <p className="text-lg mb-8 max-w-3xl mx-auto">
          Contactez-nous pour discuter de vos besoins et découvrir comment nos offres peuvent vous aider à atteindre vos objectifs de décarbonation.
        </p>
        <a href="/contact" className="btn-primary">
          Nous contacter
        </a>
      </section>
    </div>
  );
};

export default Offers;
